import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Play, Pause, RotateCcw, Volume2, VolumeX, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MusicTrack {
  id: string;
  title: string;
  audioUrl: string;
  duration?: number;
  source: 'suno' | 'mureka' | 'sonauto' | 'udio' | 'human';
}

interface MusicPlayerProps {
  track: MusicTrack;
  onLoadStart?: () => void;
  onLoadComplete?: (duration: number) => void;
  onLoadError?: (error: string) => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onListeningTimeUpdate?: (totalListeningTime: number, songDuration: number, listeningPercentage: number) => void;
  className?: string;
  autoPlay?: boolean;
  /** Maximum playback duration in seconds. Audio will stop at this point. */
  maxPlayDuration?: number;
  /** Start playback from this offset in seconds. Used for random snippet selection. */
  playStartOffset?: number;
}

export function MusicPlayer({
  track,
  onLoadStart,
  onLoadComplete,
  onLoadError,
  onPlayStateChange,
  onTimeUpdate,
  onListeningTimeUpdate,
  className,
  autoPlay = false,
  maxPlayDuration,
  playStartOffset
}: MusicPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const previousTrackIdRef = useRef<string>('');
  const preservedStateRef = useRef<{
    wasPlaying: boolean;
    currentTime: number;
    volume: number;
    isMuted: boolean;
  }>({ wasPlaying: false, currentTime: 0, volume: 1, isMuted: false });

  // Simple state preservation for tab switching only
  useEffect(() => {
    const handleVisibilityChange = () => {
      const audio = audioRef.current;
      if (document.hidden && audio) {
        // Only save state when tab becomes hidden (user switches tabs)
        preservedStateRef.current = {
          wasPlaying: !audio.paused,
          currentTime: audio.currentTime,
          volume: audio.volume,
          isMuted: audio.muted
        };
        console.log('MusicPlayer: Saved state for tab switch', preservedStateRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [totalListeningTime, setTotalListeningTime] = useState(0);
  const [lastPlayTime, setLastPlayTime] = useState<number | null>(null);
  const [lastAudioTime, setLastAudioTime] = useState<number | null>(null);

  // Reset state when track changes (but preserve playing state if same track)
  useEffect(() => {
    console.log('MusicPlayer: Track changed', {
      id: track.id,
      title: track.title,
      audioUrl: track.audioUrl,
      source: track.source
    });
    
    // Use previous track ID ref for more reliable track change detection
    const previousTrackId = previousTrackIdRef.current;
    const isSameTrack = previousTrackId === track.id && track.id !== '' && previousTrackId !== '';
    
    console.log('MusicPlayer: Track change detection', {
      previousTrackId,
      newTrackId: track.id,
      isSameTrack,
      totalListeningTime
    });
    
    if (!isSameTrack) {
      console.log('MusicPlayer: Different track detected, resetting listening time');
      // Update the previous track ID
      previousTrackIdRef.current = track.id;
      
      const audio = audioRef.current;
      // Store current state before resetting
      if (audio) {
        preservedStateRef.current = {
          wasPlaying: !audio.paused,
          currentTime: audio.currentTime,
          volume: audio.volume,
          isMuted: audio.muted
        };
      }
      
      setIsPlaying(false);
      setIsLoading(true);
      setHasError(false);
      setErrorMessage('');
      setCurrentTime(0);
      setDuration(0);
      setTotalListeningTime(0);
      setLastPlayTime(null);
      setLastAudioTime(null);
      
      if (audio) {
        audio.currentTime = 0;
        // Set track ID for reliable track change detection
        audio.setAttribute('data-track-id', track.id);
      }
    } else {
      console.log('MusicPlayer: Same track, checking for preserved state');
      // Make sure previousTrackIdRef is set even for same track
      if (previousTrackIdRef.current !== track.id) {
        previousTrackIdRef.current = track.id;
      }
      
      // Restore preserved state if available (only from current session tab switching)
      const audio = audioRef.current;
      const preserved = preservedStateRef.current;
      if (audio && preserved.wasPlaying && audio.paused && preserved.currentTime > 0) {
        console.log('MusicPlayer: Restoring playback state from tab switch', preserved);
        
        // Wait for metadata to load before restoring state
        const restoreState = () => {
          audio.currentTime = preserved.currentTime;
          audio.volume = preserved.volume;
          audio.muted = preserved.isMuted;
          setCurrentTime(preserved.currentTime);
          setVolume(preserved.volume);
          setIsMuted(preserved.isMuted);
          setIsPlaying(true);
          
          // Resume playback
          audio.play().catch((error) => {
            console.error('Failed to resume playback:', error);
            setIsPlaying(false);
          });
          
          // Clear the preserved state after restoring
          preservedStateRef.current = { wasPlaying: false, currentTime: 0, volume: 1, isMuted: false };
        };

        if (audio.readyState >= 2) { // HAVE_CURRENT_DATA
          restoreState();
        } else {
          audio.addEventListener('loadeddata', restoreState, { once: true });
        }
      }
    }
  }, [track.id, track.audioUrl]);

  // Reset listening time when resetTrigger changes (for same track)
  // Disabled for now as it's causing infinite loops
  // useEffect(() => {
  //   if (resetTrigger !== undefined) {
  //     console.log('MusicPlayer: Reset trigger activated', {
  //       trackId: track.id,
  //       resetTrigger,
  //       previousListeningTime: totalListeningTime
  //     });
  //     setTotalListeningTime(0);
  //     setLastPlayTime(null);
  //     setLastAudioTime(null);
  //   }
  // }, [resetTrigger, track.id]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadStart = () => {
      console.log('MusicPlayer: Load started for', audio.src);
      setIsLoading(true);
      setHasError(false);
      onLoadStart?.();
    };

    const handleLoadedMetadata = () => {
      const audioDuration = audio.duration;
      console.log('MusicPlayer: Metadata loaded', {
        duration: audioDuration,
        src: audio.src,
        playStartOffset
      });
      setDuration(audioDuration);
      setIsLoading(false);
      // Seek to start offset if specified
      if (playStartOffset && playStartOffset > 0 && playStartOffset < audioDuration) {
        audio.currentTime = playStartOffset;
        setCurrentTime(0); // Display relative time
      }
      onLoadComplete?.(audioDuration);
    };

    const handleCanPlay = () => {
      console.log('MusicPlayer: Can play', audio.src);
      setIsLoading(false);
      const stopTime = maxPlayDuration ? (playStartOffset ?? 0) + maxPlayDuration : undefined;
      // Don't autoplay if already at or past stop time (prevents infinite play/pause loop)
      if (autoPlay && !hasError && !(stopTime && audio.currentTime >= stopTime)) {
        console.log('MusicPlayer: Attempting autoplay');
        // Small delay to ensure audio is fully ready
        setTimeout(() => {
          // Re-check before playing in case state changed during timeout
          if (stopTime && audio.currentTime >= stopTime) return;
          audio.play().catch((error) => {
            console.error('Autoplay failed:', error);
            // Don't set error state for autoplay failures, just log it
            console.log('MusicPlayer: Autoplay blocked by browser, user must click play');
          });
        }, 100);
      }
    };

    const handleError = () => {
      const error = audio.error;
      let message = 'Failed to load audio file';
      
      console.error('MusicPlayer: Audio error occurred', {
        audioUrl: audio.src,
        errorCode: error?.code,
        errorMessage: error?.message,
        networkState: audio.networkState,
        readyState: audio.readyState
      });
      
      if (error) {
        switch (error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            message = 'Audio loading was aborted';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            message = 'Network error while loading audio';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            message = 'Audio file is corrupted or unsupported';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            message = 'Audio format not supported';
            break;
        }
      }

      setIsLoading(false);
      setHasError(true);
      setErrorMessage(message);
      onLoadError?.(message);
    };

    const handleTimeUpdate = () => {
      if (!isDragging) {
        const current = audio.currentTime;
        const offset = playStartOffset ?? 0;
        const stopTime = maxPlayDuration ? offset + maxPlayDuration : undefined;

        // Enforce max play duration limit (e.g. 29s snippet for AI songs)
        if (stopTime && current >= stopTime) {
          audio.pause();
          audio.currentTime = stopTime;
          setCurrentTime(maxPlayDuration!);
          setIsPlaying(false);
          setLastPlayTime(null);
          setLastAudioTime(null);
          onPlayStateChange?.(false);
          return;
        }

        // Display relative time (subtract offset)
        const relativeTime = offset > 0 ? current - offset : current;
        setCurrentTime(Math.max(0, relativeTime));
        onTimeUpdate?.(relativeTime, audio.duration);
        
        // Track actual listening time based on audio playback
        if (isPlaying && lastAudioTime !== null) {
          const timeDiff = current - lastAudioTime;
          // Only count forward progress (not seeking backwards)
          if (timeDiff > 0 && timeDiff < 2) { // Reasonable time jump (less than 2 seconds)
            const newTotalTime = totalListeningTime + (timeDiff * 1000); // Convert to milliseconds
            setTotalListeningTime(newTotalTime);
            
            // Calculate listening percentage
            const songDurationMs = audio.duration * 1000;
            const listeningPercentage = songDurationMs > 0 ? (newTotalTime / songDurationMs) * 100 : 0;
            
            console.log('MusicPlayer: Listening time update', {
              trackId: track.id,
              newTotalTime: Math.round(newTotalTime),
              songDurationMs: Math.round(songDurationMs),
              listeningPercentage: Math.round(listeningPercentage * 100) / 100
            });
            
            onListeningTimeUpdate?.(newTotalTime, songDurationMs, listeningPercentage);
          }
        }
        setLastAudioTime(current);
      }
    };

    const handleEnded = () => {
      console.log('MusicPlayer: Song ended');
      setIsPlaying(false);
      // Don't reset currentTime to 0 to preserve the end position
      // setCurrentTime(0);
      // audio.currentTime = 0;
      setLastPlayTime(null);
      setLastAudioTime(null);
      onPlayStateChange?.(false);
    };

    const handlePlay = () => {
      console.log('MusicPlayer: Audio started playing', {
        trackId: track.id,
        currentTime: audio.currentTime,
        totalListeningTime
      });
      setIsPlaying(true);
      setLastPlayTime(Date.now());
      setLastAudioTime(audio.currentTime);
      onPlayStateChange?.(true);
    };

    const handlePause = () => {
      console.log('MusicPlayer: Audio paused', {
        trackId: track.id,
        currentTime: audio.currentTime,
        totalListeningTime
      });
      setIsPlaying(false);
      setLastPlayTime(null);
      setLastAudioTime(null);
      onPlayStateChange?.(false);
    };

    // Add event listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      // Remove event listeners
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [track.id, autoPlay, hasError, isDragging, isPlaying, lastPlayTime, lastAudioTime, totalListeningTime, maxPlayDuration, playStartOffset, onLoadStart, onLoadComplete, onLoadError, onPlayStateChange, onTimeUpdate, onListeningTimeUpdate]);

  const handlePlay = async () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    console.log('MusicPlayer: handlePlay called, current state:', { isPlaying, paused: audio.paused });

    try {
      if (isPlaying) {
        console.log('MusicPlayer: Pausing audio');
        audio.pause();
      } else {
        // If at or past stop time, restart from offset
        const offset = playStartOffset ?? 0;
        const stopTime = maxPlayDuration ? offset + maxPlayDuration : undefined;
        if (stopTime && audio.currentTime >= stopTime) {
          audio.currentTime = offset;
          setCurrentTime(0);
        }
        console.log('MusicPlayer: Starting audio playback');
        await audio.play();
      }
    } catch (error) {
      console.error('Playback error:', error);
      setHasError(true);
      setErrorMessage('Failed to play audio. Please try again.');
      setIsPlaying(false); // Reset state on error
    }
  };

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || hasError || duration === 0) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const effectiveDuration = maxPlayDuration ?? duration;
    const percentage = clickX / rect.width;
    const relativeTime = Math.min(percentage * effectiveDuration, effectiveDuration);
    const offset = playStartOffset ?? 0;

    audio.currentTime = offset + relativeTime;
    setCurrentTime(relativeTime);
  };

  const handleRestart = () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    const offset = playStartOffset ?? 0;
    audio.currentTime = offset;
    setCurrentTime(0);
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const formatTime = (time: number): string => {
    if (!isFinite(time)) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const effectiveDuration = maxPlayDuration ?? duration;
  const progressPercentage = effectiveDuration > 0 ? (currentTime / effectiveDuration) * 100 : 0;

  return (
    <Card id="music-track-card" className={cn('w-full', className)}>
      <CardContent className="p-4">
        {/* Track Info - Hidden to avoid bias */}
        <div className="mb-3">
          <h3 className="text-base font-semibold">Music Track</h3>
          <p className="text-sm text-muted-foreground">
            Please listen and provide your assessment
          </p>
        </div>

        {/* Audio Element */}
        <audio
          ref={audioRef}
          src={track.audioUrl}
          data-track-id={track.id}
          preload="metadata"
          className="hidden"
        />

        {/* Error State */}
        {hasError && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Playback Error</span>
            </div>
            <p className="text-sm text-destructive/80 mt-1">{errorMessage}</p>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-4">
          <div
            className="w-full h-2 bg-muted rounded-full cursor-pointer relative overflow-hidden"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-primary rounded-full transition-all duration-150"
              style={{ width: `${progressPercentage}%` }}
            />
            {isLoading && (
              <div className="absolute inset-0 bg-muted animate-pulse" />
            )}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(effectiveDuration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Play/Pause Button */}
            <Button
              onClick={handlePlay}
              disabled={isLoading || hasError}
              size="lg"
              className="h-12 w-12 rounded-full"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </Button>

            {/* Restart Button */}
            <Button
              onClick={handleRestart}
              disabled={isLoading || hasError}
              variant="outline"
              size="sm"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>

          {/* Volume Controls */}
          <div className="flex items-center gap-2">
            <Button
              onClick={handleMuteToggle}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-20 h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && !hasError && (
          <div className="mt-4 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading audio...
          </div>
        )}
      </CardContent>
    </Card>
  );
}